import axios from "axios";
import { getEnvironment } from "../utils/getEnvironment";

const environment = getEnvironment();

interface BrandRaw {
    key: string;
    business: string;
    name: string;
    address: string;
    colorRed: number;
    colorGreen: number;
    colorBlue: number;
    description: string;
    wallpaperText: string;
    booking: boolean;
    reschedule: boolean;
    appointmentReschedule: number;
    cancellation: boolean;
    appointmentCancellation: number;
    reminder: boolean;
    appointmentReminder: number;
    confirmation: boolean;
    appointmentConfirmation: number;
    subscription: string;
    setupSteps: {
        branding: string;
        location: string;
        profileInfo: string;
        services: string;
        wallpaper: string;
        loyalty: string;
    };
    logo?: string | null;
    wallpaper?: string | null;
    affiliateEnabled: boolean;
    affiliateDiscount: number | null;
    isPartner?: boolean;
}

interface AffiliateDiscountInfo {
    name: string;
    discount: number;
}

export interface PartnerStats {
    referralAddress: string;
    discount: number;
    total: {
        referred: number;
        active: number;
        earnings: number;
    };
    month: {
        referred: number;
        active: number;
        earnings: number;
    };
    brands: {
        id: string;
        name: string;
        logo?: string;
        status: "Active" | "Expired";
        expires: string;
    }[];
}

export default class Brand {
    key: string;
    business?: string;
    name: string;
    address: string;
    colorRed: number;
    colorGreen: number;
    colorBlue: number;
    description: string;
    wallpaperText: string;
    booking: boolean;
    reschedule: boolean;
    appointmentReschedule: number;
    cancellation: boolean;
    appointmentCancellation: number;
    reminder: boolean;
    appointmentReminder: number;
    confirmation: boolean;
    appointmentConfirmation: number;
    subscription: string;
    setupSteps: {
        branding: string;
        location: string;
        profileInfo: string;
        services: string;
        wallpaper: string;
        loyalty: string;
    };
    logo?: string | null;
    wallpaper?: string | null;
    affiliateEnabled: boolean;
    affiliateDiscount: number | null;
    isPartner: boolean;

    constructor(data: BrandRaw) {
        this.key = data.key;
        this.business = data.business;
        this.name = data.name;
        this.address = data.address;
        this.colorRed = data.colorRed;
        this.colorGreen = data.colorGreen;
        this.colorBlue = data.colorBlue;
        this.description = data.description;
        this.wallpaperText = data.wallpaperText;
        this.wallpaper = data.wallpaper;
        this.booking = data.booking;
        this.reschedule = data.reschedule;
        this.appointmentReschedule = data.appointmentReschedule;
        this.cancellation = data.cancellation;
        this.appointmentCancellation = data.appointmentCancellation;
        this.reminder = data.reminder;
        this.appointmentReminder = data.appointmentReminder;
        this.confirmation = data.confirmation;
        this.appointmentConfirmation = data.appointmentConfirmation;
        this.subscription = data.subscription;
        this.setupSteps = data.setupSteps;
        this.logo = data.logo;
        this.affiliateEnabled = data.affiliateEnabled;
        this.affiliateDiscount = data.affiliateDiscount;
        this.isPartner = !!data.isPartner;
    }

    public static readonly update = async (
        uuid: string,
        key: string,
        colorRed: number,
        colorGreen: number,
        colorBlue: number,
        description: string,
        booking: boolean,
        reschedule: boolean,
        appointmentReschedule: number,
        cancellation: boolean,
        appointmentCancellation: number,
        reminder: boolean,
        appointmentReminder: number,
        confirmation: boolean,
        appointmentConfirmation: number,
        logo?: string | null
    ) => {
        return (await axios
            .put(
                environment.api_url + "/brand/" + key,
                {
                    colorRed,
                    colorGreen,
                    colorBlue,
                    description,
                    booking,
                    reschedule,
                    appointmentReschedule,
                    cancellation,
                    appointmentCancellation,
                    reminder,
                    appointmentReminder,
                    confirmation,
                    appointmentConfirmation,
                    logo,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch(console.error)) as boolean;
    };

    public static readonly updateAutomationSettings = async (
        uuid: string,
        key: string,
        booking: boolean,
        reschedule: boolean,
        appointmentReschedule: number,
        cancellation: boolean,
        appointmentCancellation: number,
        reminder: boolean,
        appointmentReminder: number,
        confirmation: boolean,
        appointmentConfirmation: number
    ) => {
        return (await axios
            .put(
                environment.api_url + "/brand/" + key,
                {
                    booking,
                    reschedule,
                    appointmentReschedule,
                    cancellation,
                    appointmentCancellation,
                    reminder,
                    appointmentReminder,
                    confirmation,
                    appointmentConfirmation,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch(console.error)) as boolean;
    };

    public static readonly update_subscription = async (
        uuid: string,
        key: string,
        subscription: string
    ) =>
        (await axios
            .post(
                environment.api_url + "/brand/" + key,
                {
                    subscription,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch(console.error)) as boolean;

    public static readonly update_is_set_up = async (
        uuid: string,
        key: string,
        isSetUp: boolean
    ) =>
        (await axios
            .put(
                environment.api_url + "/brand/" + key,
                {
                    isSetUp,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch(console.error)) as boolean;

    public static readonly list = async (uuid: string) =>
        (
            (await axios
                .get(environment.api_url + "/brand/list", {
                    headers: {
                        "User-Token": uuid,
                    },
                })
                .then((res) => (res.status === 200 ? res.data : undefined))
                .catch(console.error)) as BrandRaw[] | undefined
        )?.map((x) => new Brand(x));

    public static readonly info = async (uuid: string, brand: string) => {
        const data = (await axios
            .get(environment.api_url + "/brand/" + brand, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as BrandRaw | undefined;
        if (data) return new Brand(data);
        return undefined;
    };

    public favicon = (size: number) => {
        if (size <= 0) {
            return undefined;
        }
        return (
            environment.api_url +
            "/brand/" +
            this.key +
            "/favicon/" +
            size.toString()
        );
    };

    public static readonly affiliateDiscountInfo = async (
        uuid: string,
        brandKey: string
    ) => {
        return (await axios
            .get(
                environment.api_url +
                    `/brand/${brandKey}/affiliate-discount-info`,
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as AffiliateDiscountInfo | null | undefined;
    };

    public static readonly getPartnerStats = async (
        uuid: string,
        brandKey: string
    ): Promise<PartnerStats | undefined> => {
        return (await axios
            .get(`${environment.api_url}/brand/${brandKey}/partner-stats`, {
                headers: {
                    "User-Token": uuid,
                },
            })
            .then((res) => (res.status === 200 ? res.data : undefined))
            .catch(console.error)) as PartnerStats | undefined;
    };

    public static readonly updateAffiliateDiscount = async (
        uuid: string,
        brandKey: string,
        discount: number
    ): Promise<boolean> => {
        return await axios
            .post(
                `${environment.api_url}/brand/${brandKey}/affiliate-discount`,
                { discount },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch((err) => {
                console.error("Failed to update affiliate discount", err);
                return false;
            });
    };

    public static readonly updateBranding = async (
        uuid: string,
        key: string,
        {
            colorRed,
            colorGreen,
            colorBlue,
            logo,
        }: {
            colorRed: number;
            colorGreen: number;
            colorBlue: number;
            logo?: string | null;
        }
    ): Promise<boolean> => {
        return await axios
            .put(
                `${environment.api_url}/brand/${key}`,
                {
                    colorRed,
                    colorGreen,
                    colorBlue,
                    logo,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch((err) => {
                console.error("Failed to update branding", err);
                return false;
            });
    };

    public static readonly updateWallpaper = async (
        uuid: string,
        key: string,
        {
            wallpaperText,
            wallpaper,
            originalLang,
        }: {
            wallpaperText: string;
            wallpaper?: string | null;
            originalLang?: string;
        }
    ): Promise<boolean> => {
        return await axios
            .put(
                `${environment.api_url}/brand/${key}`,
                {
                    wallpaperText,
                    wallpaper,
                    originalLang,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch((err) => {
                console.error("Failed to update wallpaper", err);
                return false;
            });
    };

    public static readonly updateServicesConfigured = async (
        uuid: string,
        key: string
    ): Promise<boolean> => {
        return await axios
            .put(
                `${environment.api_url}/brand/${key}`,
                {
                    servicesConfigured: true,
                },
                {
                    headers: {
                        "User-Token": uuid,
                    },
                }
            )
            .then((res) => res.status === 200)
            .catch((err) => {
                console.error("Failed to update servicesConfigured", err);
                return false;
            });
    };
}
